import { mdiClose, mdiRefresh } from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../../../context_providers/auth/authProvider";
import { useOrderDetailCtx } from "../../../../../context_providers/order_detail_context";
import { ACTION, InventoryStatus, MODULE } from "../../../../../utils/enums";
import ModalDialog from "../../../../new_components/common/modal";
import SearchFieldWidget from "../../../../new_components/search_field";
import OrderDeviceCreateWidget from "./order_device_create";
import OrderDeviceRow, { OrderDeviceV2 } from "./order_device_row";
import DeviceUpdateWidgets from "./device_update";
import {
  ModalAlignment,
  ModalType,
  useModal,
} from "../../../../../context_providers/modal/modal_context";
import { WarehouseModel } from "../../../../../erp/inventory/models/warehouse_model";
import RawMaterialsWidget from "./order_device_raw_material_widget";
import { OrderItemModelV2 } from "../../../../../service/models/orders/order_item_model";

interface OrderDeviceSectionProps {}

const OrderDeviceSection: FunctionComponent<OrderDeviceSectionProps> = () => {
  const { setLoading, showModal, onClose } = useModal();

  const {
    order,
    setOrder,
    devices,
    getDevices,
    onCreateOrderDevices,
    onUpdateOrderRawMaterial,
    onScannedDeviceID,
    onUpdateOrderDeviceReplace,
    onUploadDevicesCSV,
  } = useOrderDetailCtx();
  const [activeDevices, setActiveDevices] = useState<OrderDeviceV2[]>([]);
  const [returnedDevice, setReturnedDevices] = useState<OrderDeviceV2[]>([]);
  const [addNew, setAddNew] = useState(false);
  const [orderView, setOrderView] = useState(false);
  const { user, isAuthorised, hasRoleTitle } = useAuth();
  const [isEditAllowed, setIsEditAllowed] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [itemId, setItemId] = useState<any>();
  const devicesFileInputRef = useRef<HTMLInputElement>(null);

  const createOrderDevices = async (data: {
    isScanned: boolean;
    device_ids: string[];
    order_id: string;
    status: string;
    org_namespace: string;
    org_name: string;
    warehouse_id?: string;
    warehouse?: WarehouseModel;
    date: Date;
    item_id: string;
    product_id: string;
  }) => {
    if (await onCreateOrderDevices(data)) setAddNew(false);
  };

  const onScan = async (data: { scanned_id: string }) => {
    if (await onScannedDeviceID(data)) setAddNew(false);
  };

  const searchDevice = (search: string) => {
    if (search) {
      setActiveDevices(
        devices.filter(
          (f) =>
            [InventoryStatus.installed, InventoryStatus.delivered].includes(
              f.status,
            ) &&
            f.device_id.trim().toLowerCase().match(search.trim().toLowerCase()),
        ),
      );
      setReturnedDevices(
        devices.filter(
          (f) =>
            [InventoryStatus.returnedNStocked].includes(f.status) &&
            f.device_id.trim().toLowerCase().match(search.trim().toLowerCase()),
        ),
      );
    } else {
      setActiveDevices(
        devices.filter((f) =>
          [InventoryStatus.installed, InventoryStatus.delivered].includes(
            f.status,
          ),
        ),
      );
      setReturnedDevices(
        devices.filter((f) =>
          [InventoryStatus.returnedNStocked].includes(f.status),
        ),
      );
    }
  };

  const onUpdateDevice = async (data: {
    device: OrderDeviceV2;
    return_strategy: boolean;
    replacement_strategy: boolean;
    replaceDevice?: OrderDeviceV2;
    warehouse?: WarehouseModel;
  }) => {
    setLoading(true);
    await onUpdateOrderDeviceReplace(data);
    await getDevices();
    setLoading(false);
  };

  const onUpdateRawMaterials = async (raw_materials: OrderItemModelV2[]) => {
    setLoading(true);
    await onUpdateOrderRawMaterial({
      order_id: order.order_id,
      raw_material: raw_materials,
    });
    setLoading(false);
  };

  const handleUploadDeviceClick = () => {
    if (devicesFileInputRef.current) {
      devicesFileInputRef.current.click();
    }
  };

  const handleDownloadTemplate = () => {
    const headers = ["Item Code", "Warehouse", "Qty"];

    const csvContent = [headers]
      .map((e) => e.map((v) => `"${v}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "devices_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUploadCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    onUploadDevicesCSV(file, order.order_id);
  };

  useEffect(() => {
    getDevices();
  }, [getDevices]);

  useEffect(() => {
    setActiveDevices(
      devices.filter((f) =>
        [
          InventoryStatus.installed,
          InventoryStatus.delivered,
          InventoryStatus.stockOut,
        ].includes(f.status),
      ),
    );
    setReturnedDevices(
      devices.filter((f) =>
        [InventoryStatus.returnedNStocked].includes(f.status),
      ),
    );
  }, [devices]);

  useEffect(() => {
    if (itemId) {
      setActiveDevices(
        devices.filter(
          (f) =>
            [
              InventoryStatus.installed,
              InventoryStatus.delivered,
              InventoryStatus.stockOut,
            ].includes(f.status) && f.item_id === itemId,
        ),
      );
      setReturnedDevices(
        devices.filter(
          (f) =>
            [InventoryStatus.returnedNStocked].includes(f.status) &&
            f.item_id === itemId,
        ),
      );
    } else {
      setActiveDevices(
        devices.filter((f) =>
          [
            InventoryStatus.installed,
            InventoryStatus.delivered,
            InventoryStatus.stockOut,
          ].includes(f.status),
        ),
      );
      setReturnedDevices(
        devices.filter((f) =>
          [InventoryStatus.returnedNStocked].includes(f.status),
        ),
      );
    }
  }, [itemId, devices]);

  useEffect(() => {
    const loggedInUser = user?.uid;
    const orderCreatedBy = order.created_by;
    const hasOrderCreatorAccess = hasRoleTitle({
      roleTitle: "Create orders only",
    });

    // Allow edit if the logged in user created the order, or has order creator role
    setIsEditAllowed(loggedInUser === orderCreatedBy || hasOrderCreatorAccess);

    // Disable if no UPDATE permission
    setDisabled(
      !isAuthorised({
        module: MODULE.order,
        action: [ACTION.UPDATE],
      }),
    );

    const order_view_permission = isAuthorised({
      module: MODULE.order_view,
      action: [ACTION.READ],
    });

    setOrderView(order_view_permission);
  }, [user, order.created_by, isAuthorised]);

  return (
    <>
      <div
        className={`  mt-10 mx-5 right-0 text-xs
                transform duration-500 ease-in-out   slide_drawer_rtl `}
      >
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-2 gap-2">
          <div className="flex items-center gap-1 flex-wrap">
            <h3 className="font-semibold text-gray-500 text-sm">
              Delivered Devices -{" "}
            </h3>

            <div className="flex gap-2">
              <div className="">
                Delivered :{" "}
                <span>
                  {
                    activeDevices.filter(
                      (f) => f.status === InventoryStatus.delivered,
                    ).length
                  }
                </span>
                {","}
              </div>
              <div className="">
                Installed :{" "}
                <span>
                  {
                    activeDevices.filter(
                      (f) => f.status === InventoryStatus.installed,
                    ).length
                  }
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-end mb-2 gap-2">
            <div className="flex flex-col">
              <label htmlFor="item">Item</label>
              <div className="flex bg-white">
                <select
                  onChange={(e) => {
                    const t = JSON.parse(e.target.value);
                    setItemId(t.item_id);
                  }}
                  value={JSON.stringify({ item_id: itemId })}
                  name=""
                  id="item"
                  className="text-xs focus:outline-none px-2 py-1 rounded"
                >
                  <option value="" hidden>
                    Select
                  </option>

                  {(order.products ?? []).map((item) => (
                    <option value={JSON.stringify({ item_id: item.item_id })}>
                      {item.item?.name ?? "--"}
                    </option>
                  ))}
                </select>
                {itemId && (
                  <button
                    className="p-1"
                    onClick={() => {
                      setItemId(null);
                    }}
                  >
                    <Icon path={mdiClose} size={0.8}></Icon>
                  </button>
                )}
              </div>
            </div>
            <div className="bg-white rounded">
              <SearchFieldWidget
                placeholder="Device Id"
                onClear={() => searchDevice("")}
                onSubmit={(search) => {
                  searchDevice(search);
                }}
              />
            </div>
            {/* {!orderView && (
              <button
                onClick={() => setAddNew(true)}
                disabled={disabled}
                className={`rounded px-2 py-1 text-sm text-white ${
                  disabled
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-myPrimaryColor"
                }`}
              >
                Add
              </button>
            )} */}
          </div>
        </div>

        <div className="hidden md:grid grid-cols-12 bg-white font-semibold  rounded-md px-3 py-2 text-xs">
          <div>Row ID | Item Code</div>

          <div>Device ID</div>
          <div>Warehouse</div>
          <div>Outward Type</div>
          <div>Voucher ID</div>
          <div>Org namespace</div>
          <div>Product</div>
          <div>Delivery Date</div>
          <div>Installation Date</div>
          <div>Returned Date</div>
          <div>Status</div>
          {/* {!orderView && (
            <div>
              <label
                onClick={handleDownloadTemplate}
                className="bg-myPrimaryColor text-white rounded-lg px-3 py-1.5 text-sm cursor-pointer duration-300 m-1"
              >
                Template
              </label>
              <input
                type="file"
                ref={devicesFileInputRef}
                accept=".csv"
                onChange={handleUploadCSV}
                className="hidden"
              />

              <button
                onClick={handleUploadDeviceClick}
                className="bg-myPrimaryColor text-white rounded-lg px-3 py-1.5 text-sm cursor-pointer duration-300 my-1"
              >
                Upload Devices
              </button>
            </div>
          )} */}
        </div>
        <div
          className="flex flex-col gap-2 mt-1 overflow-auto "
          style={{ maxHeight: "67vh", minHeight: "37vh" }}
        >
          {activeDevices.length > 0 ? (
            activeDevices.map((device, i) => (
              <OrderDeviceRow
                device={device}
                sl_no={i + 1}
                key={device.device_id + i}
                canEdit={orderView}
                isEditAllowed={isEditAllowed}
                onUpdate={(orderDevice) => {
                  showModal({
                    type: ModalType.drawer,
                    title: "Address Picker",
                    alignment: ModalAlignment.right,
                    container(id) {
                      return (
                        <DeviceUpdateWidgets
                          data={orderDevice}
                          onSubmit={onUpdateDevice}
                          onCancel={function (): void {
                            onClose(id);
                          }}
                        />
                      );
                    },
                  });
                }}
              />
            ))
          ) : (
            <div className="my-10 flex flex-col gap-5 items-center justify-center text-base font-semibold text-gray-400">
              <p> No Record Found !</p>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <h3 className="font-semibold text-gray-500 text-sm">Returned Devices</h3>
          <button onClick={() => getDevices()} className="">
            <Icon path={mdiRefresh} className="h-5 w-5"></Icon>
          </button>
        </div>
        <div className="hidden md:grid grid-cols-11 bg-white font-semibold  rounded-md px-3 py-2 text-xs">
          {/* <div>SL.NO</div> */}
          <div>Row Id | Item Code</div>
          <div>Device ID</div>
          <div>Warehouse</div>
          <div>Replaced By</div>
          <div>Replaced To</div>
          <div>Org namespace</div>
          <div>Product</div>
          <div>Delivery Date</div>
          <div>Installation Date</div>
          <div>Returned Date</div>
          <div>Status</div>
        </div>
        <div
          className="flex flex-col gap-2 mt-1 overflow-auto "
          style={{ maxHeight: "67vh", minHeight: "37vh" }}
        >
          {returnedDevice.length > 0 ? (
            returnedDevice.map((device, i) => (
              <OrderDeviceRow
                disabled={false}
                isEditAllowed={isEditAllowed}
                device={device}
                sl_no={i + 1}
                key={device.device_id + i}
                onUpdate={function (data: OrderDeviceV2): void {}}
              />
            ))
          ) : (
            <div className="my-10 flex flex-col gap-5 items-center justify-center text-base font-semibold text-gray-400">
              <p> No Record Found !</p>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <h3 className="font-semibold text-gray-500 text-sm">Raw Materials</h3>
          <button onClick={() => getDevices()} className="">
            <Icon path={mdiRefresh} className="h-5 w-5"></Icon>
          </button>
        </div>

        <div className="hidden md:block bg-white rounded-md shadow p-3 my-2 text-xs">
          <div className="grid grid-cols-7 gap-3 ">
            <h4 className="font-semibold">
              <span className="text-base text-red-500">*</span>Warehouse
            </h4>
            <h4 className="font-semibold">
              <span className="text-base text-red-500">*</span>Item Code
            </h4>
            <h4 className="font-semibold">
              <span className="text-base text-red-500">*</span>Item
            </h4>
            <h4 className="font-semibold">
              <span className="text-base text-red-500">*</span>Item Description
            </h4>

            <h4 className="font-semibold">
              <span className="text-base text-red-500">*</span>Item Unit
            </h4>
            <h4 className="font-semibold">
              <span className="text-base text-red-500">*</span>qty
            </h4>
            <h4 className="font-semibold text-center">
              <span className="text-base text-red-500">*</span>Actions
            </h4>
          </div>
        </div>
        <div
          className="flex flex-col gap-2 mt-1 overflow-auto "
          style={{ maxHeight: "67vh", minHeight: "37vh" }}
        >
          {(order.raw_materials ?? []).length > 0 ? (
            (order.raw_materials ?? []).map((item, i) => (
              <RawMaterialsWidget
                edit={false}
                canEdit={orderView}
                isEditAllowed={isEditAllowed}
                key={item.id}
                extra={item}
                onDelete={(it) => {
                  const rawMaterialData = [...order.raw_materials].filter(
                    (v, j) => v.id !== it.id,
                  );
                  // const excess = GetExcessAmt(excessData);
                  setOrder((old) => ({
                    ...old,
                    // excess,
                    raw_materials: rawMaterialData,
                  }));
                }}
                onSubmit={(data) => {
                  const rawMaterialData = order.raw_materials.map((it, j) => {
                    if (it.id === item.id) {
                      return data;
                    }
                    return it;
                  });
                  // const excess = GetExcessAmt(excessData);
                  setOrder((old) => ({
                    ...old,
                    // excess,
                    raw_materials: rawMaterialData,
                  }));
                  onUpdateRawMaterials(rawMaterialData);
                }}
              />
            ))
          ) : (
            <div className="my-10 flex flex-col gap-5 items-center justify-center text-base font-semibold text-gray-400">
              <p> No Record Found !</p>
            </div>
          )}
        </div>
      </div>
      {addNew && (
        <ModalDialog
          show={addNew}
          title="Device allocation"
          onClose={() => {
            setAddNew(false);
          }}
        >
          <div className="p-5">
            <OrderDeviceCreateWidget
              order={order!}
              onCreateOrderDevices={createOrderDevices}
              onScannedDevice={onScan}
            />
          </div>
        </ModalDialog>
      )}
    </>
  );
};

export default OrderDeviceSection;
